/**
 * `stock.*` — inventory movements, balances, and adjustments.
 *
 * Append-only movement ledger (stock_movements) + WAC-updated balance
 * projection (stock_balances). Phase 1 exposes opening balance entry
 * and stock adjustments; GRN/invoice auto-posting lands in Phase 2.
 */
import { z } from "zod";
import { uuid, paged, moneyPaise } from "../_common.js";

export const movementType = z.enum([
  "opening_balance",
  "grn",
  "sales_dispatch",
  "purchase_return",
  "sales_return",
  "transfer_in",
  "transfer_out",
  "job_work_out",
  "job_work_in",
  "adjustment_in",
  "adjustment_out",
  "production_issue",
]);
export type MovementType = z.infer<typeof movementType>;

// ── Stock summary (balance per item × warehouse) ──────────────────
export const summaryRow = z.object({
  balanceId: uuid,
  itemId: uuid,
  itemName: z.string(),
  itemSku: z.string().nullable(),
  itemUnit: z.string(),
  itemCategory: z.string().nullable(),
  warehouseId: uuid,
  warehouseName: z.string(),
  quantityOnHand: z.number().int(),
  valuationRatePaise: z.string(),   // stringified bigint
  totalValuePaise: z.string(),       // stringified bigint
  reorderLevel: z.number().int().nullable(),
  isBelowReorder: z.boolean(),
  updatedAt: z.string().datetime(),
});

export const summaryInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  warehouseId: uuid.optional(),
  itemId: uuid.optional(),         // exact item lookup (e.g. per-warehouse balance for one BOM line)
  q: z.string().optional(),        // search by item name/sku
  tab: z.enum(["all", "low_stock", "zero_stock"]).default("all"),
});
export const summaryOutput = paged(summaryRow);

// ── Stock ledger (movement history for an item) ───────────────────
export const ledgerRow = z.object({
  id: uuid,
  movementType,
  direction: z.enum(["in", "out"]),
  quantity: z.number().int(),
  ratePaise: z.string(),            // stringified bigint
  amountPaise: z.string(),          // stringified bigint
  qtyAfter: z.number().int(),
  valuationRateAfter: z.string(),   // stringified bigint
  warehouseId: uuid,
  warehouseName: z.string(),
  referenceType: z.string().nullable(),
  referenceId: uuid.nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export const ledgerInput = z.object({
  itemId: uuid,
  warehouseId: uuid.optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
});
export const ledgerOutput = paged(ledgerRow);

// ── Opening balance ───────────────────────────────────────────────
export const openingBalanceEntry = z.object({
  itemId: uuid,
  warehouseId: uuid,
  quantity: z.number().int().positive("Quantity must be positive"),
  ratePaise: moneyPaise,  // cost per unit
  notes: z.string().trim().max(500).optional(),
});

export const openingBalanceInput = z.object({
  // Reject duplicate (itemId, warehouseId) rows at the boundary — otherwise
  // `recordMovement` would run twice and weighted-average two "opening"
  // entries into one blended balance, which is never intentional.
  // (@see PR #145 review, finding #2)
  entries: z
    .array(openingBalanceEntry)
    .min(1)
    .max(100)
    .refine(
      (rows) => {
        const seen = new Set<string>();
        for (const r of rows) {
          const key = `${r.itemId}|${r.warehouseId}`;
          if (seen.has(key)) return false;
          seen.add(key);
        }
        return true;
      },
      { message: "Duplicate item + warehouse combination — merge these rows into one." },
    ),
});
export const openingBalanceOutput = z.object({
  count: z.number().int(),
  /**
   * Item ids where `trackStock` was auto-enabled on submit — the domain flips
   * it for any non-tracked item because opening-balance intent = "start
   * tracking now" (@see issue #137, mirrors declareMaterialInStock). The UI
   * surfaces this back to the user as a "tracking enabled on N item(s)" toast.
   */
  enabledTrackingItemIds: z.array(uuid).default([]),
});

// ── Stock adjustment ──────────────────────────────────────────────
export const adjustInput = z.object({
  itemId: uuid,
  warehouseId: uuid,
  direction: z.enum(["in", "out"]),
  quantity: z.number().int().positive("Quantity must be positive"),
  ratePaise: moneyPaise,
  reason: z.string().trim().min(1, "Reason is required").max(300),
  notes: z.string().trim().max(500).optional(),
});
export const adjustOutput = z.object({ id: uuid });

// ── Adjustments list ──────────────────────────────────────────────
export const adjustmentRow = z.object({
  id: uuid,
  direction: z.enum(["in", "out"]),
  quantity: z.number().int(),
  ratePaise: z.string(),
  amountPaise: z.string(),
  qtyAfter: z.number().int(),
  itemId: uuid,
  itemName: z.string(),
  itemSku: z.string().nullable(),
  itemUnit: z.string(),
  warehouseId: uuid,
  warehouseName: z.string(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export const adjustmentsInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  warehouseId: uuid.optional(),
  itemId: uuid.optional(),
  /** Server-side direction filter for the All / In / Out tabs. */
  direction: z.enum(["in", "out"]).optional(),
});

/**
 * Whole-dataset totals for the adjustments list KPI strip + tab counts.
 * Computed across ALL adjustments (respecting warehouse/item filters but NOT
 * the direction tab), so the tiles and tab badges stay stable while paging.
 */
export const adjustmentsSummary = z.object({
  total: z.number().int().nonnegative(),
  inCount: z.number().int().nonnegative(),
  outCount: z.number().int().nonnegative(),
  /** Sum of amount_paise across inbound adjustments (stringified bigint). */
  inValuePaise: z.string(),
  /** Sum of amount_paise across outbound adjustments (stringified bigint). */
  outValuePaise: z.string(),
});

export const adjustmentsOutput = paged(adjustmentRow).extend({
  summary: adjustmentsSummary,
});

// ── Warehouse movement history ────────────────────────────────────
export const warehouseMovementRow = z.object({
  id: uuid,
  movementType,
  direction: z.enum(["in", "out"]),
  quantity: z.number().int(),
  ratePaise: z.string(),
  amountPaise: z.string(),
  qtyAfter: z.number().int(),
  itemId: uuid,
  itemName: z.string(),
  itemSku: z.string().nullable(),
  itemUnit: z.string(),
  notes: z.string().nullable(),
  referenceType: z.string().nullable(),
  referenceId: uuid.nullable(),
  createdAt: z.string().datetime(),
});

export const warehouseMovementsInput = z.object({
  warehouseId: uuid,
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
});
export const warehouseMovementsOutput = paged(warehouseMovementRow);
export type WarehouseMovementRow = z.infer<typeof warehouseMovementRow>;
export type WarehouseMovementsInput = z.infer<typeof warehouseMovementsInput>;

// ── Transfers list ────────────────────────────────────────────────
export const transfersListRow = z.object({
  referenceId: uuid,
  quantity: z.number().int(),
  amountPaise: z.string(),
  itemId: uuid,
  itemName: z.string(),
  itemSku: z.string().nullable(),
  itemUnit: z.string(),
  fromWarehouseId: uuid,
  fromWarehouseName: z.string(),
  toWarehouseId: uuid,
  toWarehouseName: z.string(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export const transfersListInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  warehouseId: uuid.optional(),
  itemId: uuid.optional(),
});
export const transfersListOutput = paged(transfersListRow);
export type TransfersListRow = z.infer<typeof transfersListRow>;
export type TransfersListInput = z.infer<typeof transfersListInput>;

// ── Inter-warehouse transfer ──────────────────────────────────────
export const transferInput = z.object({
  itemId: uuid,
  fromWarehouseId: uuid,
  toWarehouseId: uuid,
  quantity: z.number().int().positive("Quantity must be positive"),
  notes: z.string().trim().max(500).optional(),
});
export const transferOutput = z.object({
  outMovementId: uuid,
  inMovementId: uuid,
});
export type TransferInput = z.infer<typeof transferInput>;

// Derived TS types
export type SummaryRow = z.infer<typeof summaryRow>;
export type SummaryInput = z.infer<typeof summaryInput>;
export type LedgerRow = z.infer<typeof ledgerRow>;
export type LedgerInput = z.infer<typeof ledgerInput>;
export type OpeningBalanceEntry = z.infer<typeof openingBalanceEntry>;
export type OpeningBalanceInput = z.infer<typeof openingBalanceInput>;
export type AdjustInput = z.infer<typeof adjustInput>;
export type AdjustmentRow = z.infer<typeof adjustmentRow>;
export type AdjustmentsInput = z.infer<typeof adjustmentsInput>;
export type AdjustmentsSummary = z.infer<typeof adjustmentsSummary>;
export type AdjustmentsOutput = z.infer<typeof adjustmentsOutput>;
